import Pagination from "@mui/material/Pagination";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { useLeadStore, usePopupStore, useViewSettingsStore } from "../..";
import useActivePage from "../../hooks/useActivePage";
import useLeads, { LeadsSortingType } from "../../hooks/useLeads";
import useLeadsOwnerFilterValues from "../../hooks/useLeadsOwnerFilterValues";
import useLeadsWithFocusArea from "../../hooks/useLeadsWithFocusArea";
import useLeadsWithGlobalFilters from "../../hooks/useLeadsWithGlobalFilters";
import useNavigateToLead from "../../hooks/useNavigateToLead";
import usePagination from "../../hooks/usePagination";
import PopupType from "../../interfaces/PopupType";
import { GlobalFiltersTab } from "../../store/FilterSettingsStore";
import { formatNumber } from "../../utils/utils";
import GlobalFiltersIcon from "../icons/GlobalFiltersIcon";
import SearchIcon from "../icons/SearchIcon";
import tabStyles from "../List.module.css";
import Spinner from "../spinners/Spinner";
import LeadInfo from "./LeadInfo";

function Leads() {
    const navigateToLead = useNavigateToLead();
    const leadStore = useLeadStore();
    const popupStore = usePopupStore();
    const viewSettingsStore = useViewSettingsStore();
    const ownerFilterValues = useLeadsOwnerFilterValues();
    const [ownerFilter, setOwnerFilter] = useState(ownerFilterValues[0]);
    const [searchFilter, setSearchFilter] = useState("");
    const [debouncedSearchFilter] = useDebounce(searchFilter, 500);
    const [sortBy, setSortBy] = useState(LeadsSortingType.ModifiedNewest);
    const [page, setPage] = useState(1);
    const leads = useLeadsWithFocusArea(useLeadsWithGlobalFilters(useLeads(ownerFilter, debouncedSearchFilter, sortBy)));
    const [pageCount, pagedLeads] = usePagination(leads, page, () => setPage(1));
    useActivePage(leads, popupStore.id!, popupStore.type === PopupType.Lead && leads.find(l => l.id === popupStore.id), searchFilter, ownerFilter, sortBy, setPage);

    useEffect(() => {
        if (!ownerFilterValues.includes(ownerFilter))
            setOwnerFilter(ownerFilterValues[0]);
    }, [ownerFilterValues, ownerFilter]);

    return (
        <div className={tabStyles.container}>
            <div className={tabStyles.header}>
                {!leadStore.isLeadsLoading.fetching && formatNumber(leads.length)} Leads
                {!viewSettingsStore.isMobile && <GlobalFiltersIcon tab={GlobalFiltersTab.Leads} className={tabStyles.headerButton} />}
            </div>
            <div className={tabStyles.filters}>
                <div className={tabStyles.search}>
                    <SearchIcon />
                    <input autoFocus={!viewSettingsStore.isMobile} placeholder="Search leads.." value={searchFilter} onChange={e => setSearchFilter(e.target.value)} />
                </div>
                <div className={tabStyles.dropdowns}>
                    <select value={ownerFilter} onChange={e => setOwnerFilter(e.target.value)}>
                        {ownerFilterValues.map(v => <option key={v}>{v}</option>)}
                    </select>
                    <select value={sortBy} onChange={e => setSortBy(e.target.value)}>
                        <option value={LeadsSortingType.LandSizeAsc}>Land Size (# - ###)</option>
                        <option value={LeadsSortingType.LandSizeDesc}>Land Size (### - #)</option>
                        <option value={LeadsSortingType.BuildingSizeAsc}>Building Size (# - ###)</option>
                        <option value={LeadsSortingType.BuildingSizeDesc}>Building Size (### - #)</option>
                        <option value={LeadsSortingType.LotCoverageAsc}>Lot Coverage (# - ###)</option>
                        <option value={LeadsSortingType.LotCoverageDesc}>Lot Coverage (### - #)</option>
                        <option value={LeadsSortingType.AssessmentAsc}>Assessment ($ - $$$)</option>
                        <option value={LeadsSortingType.AssessmentDesc}>Assessment ($$$ - $)</option>
                        <option value={LeadsSortingType.ModifiedNewest}>Modified (Newest)</option>
                        <option value={LeadsSortingType.ModifiedOldest}>Modified (Oldest)</option>
                        <option value={LeadsSortingType.ReminderDateNewest}>Reminder Date (Newest)</option>
                        <option value={LeadsSortingType.ReminderDateOldest}>Reminder Date (Oldest)</option>
                    </select>
                </div>
            </div>
            <div className={tabStyles.list}>
                {leadStore.isLeadsLoading.fetching ? (
                    <Spinner className={tabStyles.spinner} />
                ) : (
                    pagedLeads.map(l => (
                        <LeadInfo
                            key={l.id}
                            className={[
                                tabStyles.item,
                                popupStore.type === PopupType.Lead && popupStore.id === l.id
                                    ? tabStyles.opened
                                    : "",
                            ].join(" ")}
                            address={l.address}
                            ownerName={l.ownerName}
                            status={l.status}
                            buildingSize={l.buildingSize}
                            landSizeAcres={l.landSize}
                            zoning={l.zoning}
                            onClick={() => navigateToLead(l.id)}
                        />
                    ))
                )}
            </div>
            <div className={tabStyles.pagination}>
                <Pagination count={pageCount} page={page} onChange={(_, page) => setPage(page)} size="small" />
            </div>
        </div>
    );
}

export default observer(Leads);